import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VerfahrenSearchKey, VerfahrenSearchModeLabel, VerfahrenStatusLabel } from '@schir-int-client/verfahren-shared';
import { SupportsWarningDialogBeforeClose } from '@schir-int-client/tech';

interface FilterChip {
	value: string;
	label: string;
}

interface FilterGroup {
	chips: FilterChip[];
	selected: Set<string>;
}

export interface VerfahrenSearchFilterDialogData {
	searchFields: VerfahrenSearchKey[];
	selectedSearchFields: string[];
	selectedStatus: string[];
}

export interface VerfahrenSearchFilterDialogResult {
	searchFields: string[];
	status: string[];
}

@Component({
	selector: 'schir-int-client-verfahren-search-filter-dialog',
	templateUrl: './verfahren-search-filter-dialog.component.html',
	styleUrls: ['./verfahren-search-filter-dialog.component.scss'],
	standalone: false,
})
export class VerfahrenSearchFilterDialogComponent extends SupportsWarningDialogBeforeClose {
	/**
	 * Feste Anzeige-Reihenfolge der Suchfelder-Chips (fachlich vorgegeben).
	 * SchiR- und eLuPfand-Felder sind hier kombiniert, da pro Verfahren jeweils nur die
	 * relevanten Felder in searchFields enthalten sind und die Reihenfolge innerhalb jeder
	 * Teilmenge dadurch trotzdem korrekt erhalten bleibt.
	 */
	private static readonly SEARCH_FIELD_ORDER: string[] = [
		'common.short_aktenzeichen',
		'schiff.unterscheidungssignal',
		'schiff.imo_nummer',
		'schiff.schiff_name',
		'schiff.historische_schiff_namen',
		'lfpr.blattnummer',
		'lfpr.eintragungszeichen',
		'lfpr.muster',
		'lfpr.werknummer',
		'lfpr.historische_blattnummer_rolle',
		'lfpr.historisches_eintragungszeichen',
		'common.eigentuemer_namen',
		'lfpr.glaeubiger',
		'common.assigned_kontakte',
		'lfpr.ar_kennzeichen',
		'lfpr.standort_akte',
		'common.aufschrift',
	];

	private static readonly FORCE_NEW_LINE_BEFORE: string[] = [
		'schiff.schiff_name',
		'common.eigentuemer_namen',
		'lfpr.historisches_eintragungszeichen',
		'lfpr.ar_kennzeichen',
	];

	@Output() public doApplyFilters: EventEmitter<VerfahrenSearchFilterDialogResult> = new EventEmitter();

	public readonly searchFieldChips: FilterChip[] = (this.data?.searchFields ?? [])
		.map(value => ({ value, label: value in VerfahrenSearchModeLabel ? VerfahrenSearchModeLabel[value] : value }))
		.sort((a, b) => this.searchFieldSortIndex(a.value) - this.searchFieldSortIndex(b.value));

	public readonly statusFilterChips: FilterChip[] = Object.entries(VerfahrenStatusLabel).map(([value, label]) => ({
		value,
		label,
	}));

	private readonly initialSelectedSearchFields = new Set<string>(this.data?.selectedSearchFields ?? []);
	private readonly initialSelectedStatus = new Set<string>(this.data?.selectedStatus ?? []);

	private readonly filterGroups: Record<string, FilterGroup> = {
		searchFields: { chips: this.searchFieldChips, selected: new Set<string>(this.initialSelectedSearchFields) },
		status: { chips: this.statusFilterChips, selected: new Set<string>(this.initialSelectedStatus) },
	};

	public constructor(
		private dialogRef: MatDialogRef<VerfahrenSearchFilterDialogComponent>,
		@Inject(MAT_DIALOG_DATA) private data: VerfahrenSearchFilterDialogData
	) {
		super();
	}

	private searchFieldSortIndex(value: string): number {
		const index = VerfahrenSearchFilterDialogComponent.SEARCH_FIELD_ORDER.indexOf(value);
		return index === -1 ? Number.MAX_SAFE_INTEGER : index;
	}

	private setsAreEqual(a: Set<string>, b: Set<string>): boolean {
		return a.size === b.size && Array.from(a).every(value => b.has(value));
	}

	public isDirty(): boolean {
		return (
			!this.setsAreEqual(this.filterGroups.searchFields.selected, this.initialSelectedSearchFields) ||
			!this.setsAreEqual(this.filterGroups.status.selected, this.initialSelectedStatus)
		);
	}

	public isValid(): boolean {
		return !this.hasErrors();
	}

	public reset(): void {
		this.filterGroups.searchFields.selected = new Set<string>(this.initialSelectedSearchFields);
		this.filterGroups.status.selected = new Set<string>(this.initialSelectedStatus);
	}

	public async submit(): Promise<boolean> {
		if (this.hasErrors()) {
			return false;
		}
		this.doApplyFilters.emit(<VerfahrenSearchFilterDialogResult>{
			searchFields: Array.from(this.filterGroups.searchFields.selected),
			status: Array.from(this.filterGroups.status.selected),
		});
		return true;
	}

	public isSelected(groupKey: string, chip: FilterChip): boolean {
		return this.filterGroups[groupKey].selected.has(chip.value);
	}

	public allSelected(groupKey: string): boolean {
		const group = this.filterGroups[groupKey];
		return group.chips.every(chip => group.selected.has(chip.value));
	}

	public noneSelected(groupKey: string): boolean {
		return this.filterGroups[groupKey].selected.size === 0;
	}

	public hasErrors(): boolean {
		return Object.keys(this.filterGroups).some(groupKey => this.noneSelected(groupKey));
	}

	public toggleAllLabel(groupKey: string): string {
		return this.allSelected(groupKey) ? 'Alle abwählen' : 'Alle auswählen';
	}

	public toggleAll(groupKey: string): void {
		const group = this.filterGroups[groupKey];
		if (this.allSelected(groupKey)) {
			group.selected.clear();
		} else {
			group.chips.forEach(chip => group.selected.add(chip.value));
		}
	}

	public toggleFilter(groupKey: string, chip: FilterChip): void {
		const group = this.filterGroups[groupKey];
		if (group.selected.has(chip.value)) {
			group.selected.delete(chip.value);
		} else {
			group.selected.add(chip.value);
		}
	}

	public cancel(): void {
		this.onClickButtonClose.emit();
	}

	public async applyFilters(): Promise<void> {
		if (await this.submit()) {
			this.dialogRef.close();
		}
	}

	protected forceNewLineBefore(value: string): boolean {
		return VerfahrenSearchFilterDialogComponent.FORCE_NEW_LINE_BEFORE.includes(value);
	}
}
